const MS_IN_DAY = 1000 * 60 * 60 * 24;

export const DEAD_STOCK_THRESHOLD_DAYS = 180;

export type DeadStockActivitySource =
  | 'sale'
  | 'movement'
  | 'on_hold'
  | 'product_created'
  | 'unknown';

export interface DeadStockRow {
  id: string;
  name: string;
  code: string;
  category: string;
  quantity: number;
  daysInStock: number;
  value: number;
  lastActivityAt: string | null;
  lastActivitySource: DeadStockActivitySource;
}

interface ProductLike {
  id: string;
  name: string | null;
  item_code: string | null;
  category: string | null;
  quantity_on_hand: number | null;
  current_cost: number | null;
  initial_cost: number | null;
  last_sale_at?: string | null;
  created_at?: string | null;
}

interface OnHoldLike {
  product_id: string | null;
  status?: string | null;
  date_on_hold?: string | null;
}

interface RecentSaleLike {
  sale_items?: Array<{ product_id: string | null } | null> | null;
}

interface MovementLike {
  product_id: string | null;
  created_at?: string | null;
}

export interface CalculateDeadStockInput {
  products: ProductLike[];
  activeOnHoldItems: OnHoldLike[];
  recentOnHoldItems: OnHoldLike[];
  allOnHoldItems: OnHoldLike[];
  recentSales: RecentSaleLike[];
  movementHistory: MovementLike[];
  thresholdDays?: number;
  now?: Date;
}

export interface DeadStockReport {
  items: DeadStockRow[];
  totalValue: number;
}

export interface DeadStockCategoryGroup {
  category: string;
  itemCount: number;
  totalQuantity: number;
  totalValue: number;
  averageDaysInStock: number;
  items: DeadStockRow[];
}

function toTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function buildLatestTimestampMap(
  rows: Array<{ product_id: string | null }>,
  getDate: (row: { product_id: string | null }) => string | null | undefined,
): Map<string, number> {
  const map = new Map<string, number>();

  rows.forEach((row) => {
    const productId = row.product_id;
    if (!productId) return;

    const ts = toTimestamp(getDate(row));
    if (ts === null) return;

    const prev = map.get(productId);
    if (prev === undefined || ts > prev) {
      map.set(productId, ts);
    }
  });

  return map;
}

function resolveLastActivity(
  product: ProductLike,
  latestMovementByProduct: Map<string, number>,
  latestOnHoldByProduct: Map<string, number>,
): { timestamp: number | null; source: DeadStockActivitySource } {
  const candidates: Array<{ timestamp: number; source: DeadStockActivitySource }> = [];

  const saleTs = toTimestamp(product.last_sale_at ?? null);
  if (saleTs !== null) candidates.push({ timestamp: saleTs, source: 'sale' });

  const movementTs = latestMovementByProduct.get(product.id);
  if (movementTs !== undefined) {
    candidates.push({ timestamp: movementTs, source: 'movement' });
  }

  const onHoldTs = latestOnHoldByProduct.get(product.id);
  if (onHoldTs !== undefined) {
    candidates.push({ timestamp: onHoldTs, source: 'on_hold' });
  }

  const createdTs = toTimestamp(product.created_at ?? null);
  if (createdTs !== null) {
    candidates.push({ timestamp: createdTs, source: 'product_created' });
  }

  if (candidates.length === 0) {
    return { timestamp: null, source: 'unknown' };
  }

  let latest = candidates[0];
  for (let i = 1; i < candidates.length; i += 1) {
    if (candidates[i].timestamp > latest.timestamp) {
      latest = candidates[i];
    }
  }

  return latest;
}

export function formatDeadStockActivitySource(source: DeadStockActivitySource): string {
  switch (source) {
    case 'sale':
      return 'Satış';
    case 'movement':
      return 'Stok hareketi';
    case 'on_hold':
      return 'Bekleme kaydı';
    case 'product_created':
      return 'Ürün oluşturma';
    default:
      return 'Bilinmiyor';
  }
}

export function calculateDeadStockReport({
  products,
  activeOnHoldItems,
  recentOnHoldItems,
  allOnHoldItems,
  recentSales,
  movementHistory,
  thresholdDays = DEAD_STOCK_THRESHOLD_DAYS,
  now = new Date(),
}: CalculateDeadStockInput): DeadStockReport {
  const nowMs = now.getTime();
  const deadStockItems: DeadStockRow[] = [];
  let deadStockTotalValue = 0;

  const productsWithActiveOnHold = new Set<string>();
  activeOnHoldItems.forEach((row) => {
    // Defensive: if status is not provided by caller, treat the row as active.
    const status = row.status?.toLowerCase().trim();
    if ((!status || status === 'on_hold') && row.product_id) {
      productsWithActiveOnHold.add(row.product_id);
    }
  });

  const productsWithRecentSales = new Set<string>();
  recentSales.forEach((sale) => {
    sale.sale_items?.forEach((item) => {
      const productId = item?.product_id;
      if (productId) {
        productsWithRecentSales.add(productId);
      }
    });
  });

  const productsWithRecentOnHold = new Set<string>();
  recentOnHoldItems.forEach((row) => {
    if (row.product_id) {
      productsWithRecentOnHold.add(row.product_id);
    }
  });

  const latestMovementByProduct = buildLatestTimestampMap(
    movementHistory,
    (row) => (row as MovementLike).created_at ?? null,
  );
  const latestOnHoldByProduct = buildLatestTimestampMap(
    allOnHoldItems,
    (row) => (row as OnHoldLike).date_on_hold ?? null,
  );

  products.forEach((product) => {
    const quantity = Number(product.quantity_on_hand ?? 0);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    const hasActiveOnHold = productsWithActiveOnHold.has(product.id);
    const hasRecentSale = productsWithRecentSales.has(product.id);
    const hasRecentOnHold = productsWithRecentOnHold.has(product.id);
    const hasRecentActivity = hasRecentSale || hasRecentOnHold;

    const lastActivity = resolveLastActivity(product, latestMovementByProduct, latestOnHoldByProduct);
    const daysSinceLastActivity =
      lastActivity.timestamp === null
        ? 999
        : Math.max(0, Math.floor((nowMs - lastActivity.timestamp) / MS_IN_DAY));

    if (hasRecentActivity || hasActiveOnHold || daysSinceLastActivity <= thresholdDays) {
      return;
    }

    const unitCost = Number(product.current_cost ?? product.initial_cost ?? 0);
    const stockValue = Math.round(quantity * (Number.isFinite(unitCost) ? unitCost : 0));
    deadStockTotalValue += stockValue;

    deadStockItems.push({
      id: product.id,
      name: product.name || 'İsimsiz Ürün',
      code: product.item_code || 'Kod Yok',
      category: product.category || 'Kategorisiz',
      quantity,
      daysInStock: daysSinceLastActivity,
      value: stockValue,
      lastActivityAt:
        lastActivity.timestamp === null ? null : new Date(lastActivity.timestamp).toISOString(),
      lastActivitySource: lastActivity.source,
    });
  });

  deadStockItems.sort(
    (a, b) =>
      b.daysInStock - a.daysInStock ||
      b.value - a.value ||
      a.name.localeCompare(b.name, 'tr-TR'),
  );

  return {
    items: deadStockItems,
    totalValue: Math.round(deadStockTotalValue),
  };
}

export function groupDeadStockByCategory(items: DeadStockRow[]): DeadStockCategoryGroup[] {
  const categoryMap = new Map<string, DeadStockCategoryGroup>();

  items.forEach((item) => {
    const category = item.category || 'Kategorisiz';
    const existing = categoryMap.get(category);

    if (!existing) {
      categoryMap.set(category, {
        category,
        itemCount: 1,
        totalQuantity: item.quantity,
        totalValue: item.value,
        averageDaysInStock: item.daysInStock,
        items: [item],
      });
      return;
    }

    existing.itemCount += 1;
    existing.totalQuantity += item.quantity;
    existing.totalValue += item.value;
    existing.items.push(item);
    existing.averageDaysInStock = Math.round(
      existing.items.reduce((sum, row) => sum + row.daysInStock, 0) / existing.items.length,
    );
  });

  return Array.from(categoryMap.values())
    .map((group) => ({
      ...group,
      items: [...group.items].sort(
        (a, b) =>
          b.daysInStock - a.daysInStock ||
          b.value - a.value ||
          a.name.localeCompare(b.name, 'tr-TR'),
      ),
    }))
    .sort(
      (a, b) =>
        b.totalValue - a.totalValue ||
        b.itemCount - a.itemCount ||
        a.category.localeCompare(b.category, 'tr-TR'),
    );
}
